'use client';

import { RefinementListProps, useRefinementList, UseRefinementListProps } from 'react-instantsearch';
import { FilterMultiSelect, FilterMultiSelectProps } from '@workspace/ui/common';
import { useEffect, useMemo, useRef, useCallback } from 'react';
import { useInstantSearchLoading } from './LoadingIndicator';

type MenuSelectProps = {
  menuProps: UseRefinementListProps;
  beforeSelect?: (values: string[]) => boolean;
  multiSelect?: boolean;
  fullWidth?: boolean;
  filterValues?: Set<string> | string[];
  debounceMs?: number;
} & PartialKeys<FilterMultiSelectProps, 'options' | 'icon'>;

const transformItems: RefinementListProps['transformItems'] = (items) => {
  return items.map((item, idx) => ({
    ...item,
    id: `algolia-menu-select-${idx}-${item.value}`,
  }));
};

export function AlgoliaMenuSelect({
  menuProps,
  beforeSelect,
  multiSelect = true,
  maxDisplayCount = 1,
  onSelect,
  icon = 'Filter',
  fullWidth = false,
  filterValues,
  debounceMs = 300,
  ...filterProps
}: MenuSelectProps) {
  const { items, refine } = useRefinementList({
    limit: 100,
    transformItems,
    sortBy: ['name'],
    ...menuProps,
  });
  const { isLoading } = useInstantSearchLoading();
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);

  const selectedValues = useMemo(() => {
    const refined = items.filter((item) => item.isRefined).map((item) => item.value);
    const availableValues = items.map((item) => item.value);
    return refined.filter((val) => availableValues.includes(val));
  }, [items]);

  useEffect(() => {
    const availableValues = items.map((item) => item.value);
    const invalidSelections = items.filter((item) => item.isRefined && !availableValues.includes(item.value));
    invalidSelections.forEach((item) => refine(item.value));
  }, [items, refine]);

  const applyRefinements = useCallback(
    (values: string[]) => {
      items.forEach((item) => {
        const isSelected = values.includes(item.value);
        if (isSelected && !item.isRefined) {
          refine(item.value);
        } else if (!isSelected && item.isRefined) {
          refine(item.value);
        }
      });
    },
    [items, refine],
  );

  const handleSelect = useCallback(
    (values: string[]) => {
      if (beforeSelect && !beforeSelect(values)) return;

      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }

      debounceTimerRef.current = setTimeout(() => {
        applyRefinements(values);
        onSelect?.(values);
      }, debounceMs);
    },
    [beforeSelect, applyRefinements, onSelect, debounceMs],
  );

  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  const availableOptions = useMemo(() => {
    let filtered = items.filter((item) => item.count > 0 || item.isRefined);

    if (filterValues) {
      const filterSet = Array.isArray(filterValues) ? new Set(filterValues) : filterValues;

      if (filterSet.size === 0) {
        filtered = [];
      } else {
        filtered = filtered.filter((item) => filterSet.has(item.value));
      }
    }

    return filtered;
  }, [items, filterValues]);

  return (
    <FilterMultiSelect
      icon={icon}
      maxDisplayCount={maxDisplayCount}
      {...filterProps}
      syncExternalValue
      options={availableOptions}
      onSelect={handleSelect}
      value={selectedValues}
      multiSelect={multiSelect}
      fullWidth={fullWidth}
      disabled={isLoading}
    />
  );
}
